import { Box, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import FlagCountry from 'src/components/common/FlagCountry';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';

// Utilities
import dayjs from 'dayjs';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonGroupFilter from 'src/components/common/ButtonGroupFilter';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import LinkToCompanyDetail, { IForwardedParamsCompanyDetailLink } from 'src/components/LinkToCompanyDetail';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryCheckCompanyTabIsExist } from 'src/services/queries';
import { useQueryListCTDBOLs } from 'src/services/queries/useQueryCompanyTradeData';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import {
  FORMAT_DATE_TIME,
  LIMIT_GET_LIST_BILLS_OF_LADING,
} from 'src/utils/constants/config';
import { KeyFilterChartCTDRelationshipView, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';
import TooltipExplainNoTradeValue from 'src/components/common/TooltipExplainNoTradeValue';

const CompanyTradeDataBillsOfLading = ({ company }: { company: ICompanyDetail }) => {
  const { id: companyId } = useParams();
  const { setSort, ctdBillsOfLadingListSortParams } = useTradeDataSortStore();
  const { checkRestriction } = useProvisionRestriction();
  const navigate = useNavigate();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const { t } = useTranslation();
  const queryParams = useQueryParams() as IForwardedParamsCompanyDetailLink;

  const checkCompanyTabIsExistQuery = useQueryCheckCompanyTabIsExist({
    company_id: companyId,
    params: {
      company_id: companyId,
      start_date:
        queryParams?.start_date ??
        dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
      end_date:
        queryParams?.end_date ??
        dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    },
  });

  const [relationshipWithCurrentCompany, setRelationshipWithCurrentCompany] = useState<any>(
    queryParams.viewedAsType === TypeBuyerSupplier.buyer
      ? KeyFilterChartCTDRelationshipView.supplier
      : KeyFilterChartCTDRelationshipView.buyer,
  );
  // Reverted meaning with relationshipWithCurrentCompany
  const isViewingCurrentCompanyAsBuyer = relationshipWithCurrentCompany === KeyFilterChartCTDRelationshipView.supplier;

  const listSortField = useMemo(() => {
    if (ctdBillsOfLadingListSortParams?.sortBy === 'company_name') {
      return isViewingCurrentCompanyAsBuyer ? 'supplier_name' : 'buyer_name';
    }
    return ctdBillsOfLadingListSortParams?.sortBy;
  }, [ctdBillsOfLadingListSortParams?.sortBy, isViewingCurrentCompanyAsBuyer]);

  const [initDateFrom, initDateTo] = useMemo(() => {
    return [
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD'),
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD'),
    ];
  }, []);

  const [fromTime, setFromTime] = useState(queryParams.start_date || initDateFrom.format(FORMAT_DATE_TIME.yyyy_mm_dd));
  const [toTime, setToTime] = useState(queryParams.end_date || initDateTo.format(FORMAT_DATE_TIME.yyyy_mm_dd));
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(LIMIT_GET_LIST_BILLS_OF_LADING);

  const { data, isFetching } = useQueryListCTDBOLs(companyId || '', {
    start_date: dayjs(fromTime).format(FORMAT_DATE_TIME.yyyy_mm_dd),
    end_date: dayjs(toTime).format(FORMAT_DATE_TIME.yyyy_mm_dd),
    company_id: companyId || '',
    is_buyer: isViewingCurrentCompanyAsBuyer,
    sort_by: listSortField,
    order: ctdBillsOfLadingListSortParams?.sort,
    limit,
    offset,
  });

  useEffect(() => {
    if (checkCompanyTabIsExistQuery.isFetching) return;
    if (!checkCompanyTabIsExistQuery.data.data?.has_bol_tab) return;
    if (queryParams.viewedAsType) return;
    setRelationshipWithCurrentCompany(
      checkCompanyTabIsExistQuery.data.data?.is_supplier
        ? KeyFilterChartCTDRelationshipView.buyer
        : KeyFilterChartCTDRelationshipView.supplier,
    );
  }, [checkCompanyTabIsExistQuery.data.data, checkCompanyTabIsExistQuery.isFetching]);

  const { data: listBOLs = [] } = data;
  const redirectBolDetail = (item: ICompanyTradeData) => {
    navigate(AuthRouteKeys.BillOfLadingDetail.replace(':id?', item?.id));
  };

  const configColumn: ConfigCell<ICompanyTradeData>[] = [
    {
      disablePadding: false,
      id: 'bol',
      onClick: (data) => redirectBolDetail(data),
      label: 'Bill of Lading',
      cell: (data: ICompanyTradeData) => {
        const checkRestrictionBol = checkRestriction({
          countryCode: company?.country || '',
          typeData: LIST_RESTRICTION_DATA.BOL,
        });
        return (
          <Typography variant="body2" color="secondary">
            {renderTextOrDefault(data?.bol)}
            {isEmpty(data?.bol) && checkRestrictionBol && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'actual_arrival_date',
      label: 'Date',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color="text.secondary">
          {dayjs(data?.actual_arrival_date?.toString(), FORMAT_DATE_TIME.yyyy_mm_dd).format(
            FORMAT_DATE_TIME.FULL_ONLY_DATE,
          )}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'company_name',
      label: isViewingCurrentCompanyAsBuyer ? 'Supplier Name' : 'Buyer Name',
      disableSort: true,
      cell: (data: ICompanyTradeData) => {
        const companyName =
          relationshipWithCurrentCompany === KeyFilterChartCTDRelationshipView.buyer
            ? data.buyer_name
            : data.supplier_name;
        const companyCountryCode =
          relationshipWithCurrentCompany === KeyFilterChartCTDRelationshipView.buyer
            ? data.buyer_country_code
            : data.supplier_country_code;
        const companyId =
          relationshipWithCurrentCompany === KeyFilterChartCTDRelationshipView.buyer ? data.buyer_id : data.supplier_id;
        return (
          <Stack direction="row" spacing={0.5}>
            <FlagCountry code={companyCountryCode || ''} />
            <LinkToCompanyDetail
              id={companyId}
              companyName={companyName || ''}
              params={queryParams}
              viewedAsType={isViewingCurrentCompanyAsBuyer ? TypeBuyerSupplier.supplier : TypeBuyerSupplier.buyer}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'hsCode',
      label: 'HS Code',
      disableSort: true,
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color="text.secondary">
          {data?.hs_code}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: 'Weight (KG)',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data?.weight)}
          {isEqual(data?.weight, 0) && <TooltipExplainNoTradeValue />}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: 'Quantity',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data?.quantity)}
          {isEqual(data?.quantity, 0) && <TooltipExplainNoTradeValue />}
        </Typography>
      ),
    },
  ];

  return (
    <WhiteBoxContent>
      <Stack justifyContent={'space-between'} flexDirection={'row'}>
        <Typography textAlign={'left'} variant="h5">
          Most Recent Bills Of Lading
        </Typography>
        <Stack gap={3} direction={'row'} alignItems={'center'}>
          <ButtonGroupFilter
            size="small"
            label="Relationship with"
            buttons={[
              {
                label: 'SUPPLIER',
                key: KeyFilterChartCTDRelationshipView.supplier,
              },
              {
                label: 'BUYER',
                key: KeyFilterChartCTDRelationshipView.buyer,
              },
            ]}
            active={relationshipWithCurrentCompany}
            onClick={(value) => {
              setRelationshipWithCurrentCompany(value);
            }}
          />
          <Stack maxWidth={300} direction={'row'} alignItems={'center'} gap={1}>
            <DatePickerWithDefaultIcon
              value={dayjs(fromTime)}
              disableFuture={true}
              minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              // disabled={true}
              onChange={(value) => setFromTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd))}
              label={t('trade_data.search_form.input.from')}
              format={FORMAT_DATE_TIME.MONTH_YEAR}
              slotProps={{
                textField: {
                  size: 'small',
                  required: true,
                },
              }}
              views={['month', 'year']}
            />
            -
            <DatePickerWithDefaultIcon
              value={dayjs(toTime)}
              format={FORMAT_DATE_TIME.MONTH_YEAR}
              minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              disableFuture={true}
              // disabled={true}
              onChange={(value) => setToTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd))}
              label={t('trade_data.search_form.input.trade_to')}
              slotProps={{
                textField: {
                  size: 'small',
                  required: true,
                },
              }}
              views={['month', 'year']}
            />
          </Stack>
        </Stack>
      </Stack>
      <Box mt={3} />
      {!isFetching && isEmpty(getArrayDefault(listBOLs)) ? (
        <CompanyInfoNoData
          title={t('trade_data.common.notice_no_data.title')}
          content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
          sxContainer={{
            border: 'none',
          }}
        />
      ) : (
        <Table
          maxRow={10}
          rowsPerPageOptions={[10, 50, 100]}
          handleSort={(data) => {
            setSort('ctdBillsOfLadingListSortParams', data);
          }}
          onChangePage={(value) => {
            setOffset(value);
          }}
          onChangePageSize={(value) => {
            setOffset(0);
            setLimit(value);
          }}
          // onRowClick={redirectBolDetail}
          loading={isFetching}
          page={offset}
          pageSize={limit}
          totalRecords={data?.total_records}
          dataKey={(item) => item.id}
          data={listBOLs as any}
          configColumn={configColumn}
        />
      )}
    </WhiteBoxContent>
  );
};

export default CompanyTradeDataBillsOfLading;
